// Core
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Styles
import "./styles.scss";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  addStaffsRequest_ADMIN,
  fileUploadRequest_ADMIN,
  getCountryCode,
  updateStaffDetailsRequest_ADMIN,
} from "../../../../Redux/Slices/Admin/adminSlice";

// Custom layout
import MainLayout from "Layouts/Main";

// Assets
import Colors from "../../../../Assets/SassConstants/colors.scss";
import Back from "../../../../Assets/Svg/Admin/AssessmentBacklog/backChevron.svg";

// Utils
import { validateInput } from "Services/Utils/validateInput";
import { notifySuccess } from "Services/Utils/toastNotification";

// Custom component
import InputBox from "Components/InputBox";
import ErrorMessage from "Components/ErrorMessage";
import Dropdown from "Components/Dropdown";
import PhotoUpload from "Components/PhotoUpload";
import CustomSelect from "Components/CustomSelect";
import Loader from "Components/Loader";

const inputStyle = {
  borderWidth: 1,
  borderColor: Colors.grey_18,
  fontSize: 13,
  color: Colors.black_1,
  boxShadow: "none",
};
const AddUpdateStaff = () => {
  const [countryCode, setCountryCode] = useState([]);
  const [countryCodeValue, setCountryCodeValue] = useState("");
  // Initialize useNavigate
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedStaffDetails } = location.state || {};
  const isLoading = useSelector((state) => state.admin.isLoading);

  //Initialize redux
  const dispatch = useDispatch();

  const customStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #b2bec3",
      borderRadius: "6px",
      padding: "4px",
      boxShadow: "none",
      minHeight: "56px",
      height: "56px",
      cursor: "pointer",
      boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#1F2155"
        : state.isFocused
        ? "#e9ecef"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      padding: 10,
      "&:hover": {
        backgroundColor: state.isSelected
          ? "#1F2155"
          : state.isFocused
          ? "#e9ecef"
          : "#fff",
        color: state.isSelected ? "#fff" : "#000",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#000000",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#121212",
      padding: "0 8px",
    }),
  };

  //==================== Staff details start ====================
  const initialFormData = {
    employeeName: "",
    employeeId: "",
    mobileNumber: "",
    email: "",
    role: "",
    linkedInProfileLink: "",
    bio: "",
    photo: "",
  };

  const initialFormErrors = {};

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormErrors);

  let hasSelectedStaffDetails = selectedStaffDetails
    ? Object.keys(selectedStaffDetails).length > 0
    : false;

  useEffect(() => {
    if (Object?.keys(selectedStaffDetails)?.length > 0) {
      const matchedCountryCode = countryCode.find(
        (country_code) => country_code.value === selectedStaffDetails.country_code
      );
      setCountryCodeValue(matchedCountryCode || null); 
      setFormData((prevData) => ({
        ...prevData,
        employeeName: selectedStaffDetails?.user_name,
        employeeId: selectedStaffDetails.user_code,
        mobileNumber: selectedStaffDetails.mobile,
        email: selectedStaffDetails?.email,
        role: selectedStaffDetails?.role,
        linkedInProfileLink: selectedStaffDetails?.linkedInUrl,
        bio: selectedStaffDetails?.bio,
        photo: selectedStaffDetails?.profile_image,
      }));
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        employeeName: "",
        employeeId: "",
        mobileNumber: "",
        email: "",
        role: "",
        linkedInProfileLink: "",
        bio: "",
        photo: "",
      }));
    }
  }, [countryCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleValidation = () => {
    let validationErrors = {};

    Object.keys(formData).forEach((key) => {
      // skip optional fields unless they have a value
      if (
        (key === "photo" || key === "linkedInProfileLink") &&
        !formData[key]
      ) {
        return;
      }
      const error = validateInput(key, formData[key], 
        key === "mobileNumber" ? countryCodeValue : null);
  
        if (Object.keys(error).length > 0) {
          validationErrors = { ...validationErrors, ...error };
        }
    });

    setFormErrors(validationErrors);
    return validationErrors;
  };

  useEffect(() => {
    getCountryCodeData();
  }, []);

  const getCountryCodeData = () => {
    dispatch(
      getCountryCode({
        callBack: getAllCountryCode,
      })
    );
  };

  const getAllCountryCode = (data) => {
    const countryList = data?.map((country) => {
      return {
        label: `${country.country_code} ${country.country_name}`,
        value: country.country_code,
        digit_length: country.digit_length,
      };
    });
    setCountryCode(countryList);

    if (selectedStaffDetails?.country_code) {
    const matched = countryList.find(
      (code) => code.value === selectedStaffDetails.country_code
    );
    setCountryCodeValue(matched || null); 
  }
  };

  // Form submit handler
  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = handleValidation();
    if (Object.keys(validationErrors).length === 0) {
      const sanitizedData = {
        ...formData,
        photo: formData.photo || null,
        linkedInProfileLink: formData.linkedInProfileLink || null,
      };
      // Handle form submission
      if (Object.keys(selectedStaffDetails)?.length > 0) {
        dispatch(
          updateStaffDetailsRequest_ADMIN({
            userId: selectedStaffDetails?.user_id,
            data: {
              user_name: sanitizedData?.employeeName,
              user_code: sanitizedData?.employeeId,
              mobile: sanitizedData?.mobileNumber,
              country_code:countryCodeValue?.value,
              email: sanitizedData?.email,
              role: sanitizedData?.role,
              linkedInUrl: sanitizedData?.linkedInProfileLink,
              bio: sanitizedData?.bio,
              profile_image: sanitizedData?.photo,
            },
            callBack: updateStaffDetailsRequestCB_ADMIN,
          })
        );
      } else {
        dispatch(
          addStaffsRequest_ADMIN({
            data: {
              user_name: sanitizedData?.employeeName,
              user_code: sanitizedData?.employeeId,
              mobile: sanitizedData?.mobileNumber,
              country_code:countryCodeValue?.value,
              email: sanitizedData?.email,
              role: sanitizedData?.role,
              linkedInUrl: sanitizedData?.linkedInProfileLink,
              bio: sanitizedData?.bio,
              profile_image: sanitizedData?.photo,
            },
            callBack: addStaffsRequestCB_ADMIN,
          })
        );
      }
    }
  };

  const addStaffsRequestCB_ADMIN = () => {
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    notifySuccess("Staff added successfully!");
    navigate("/staff-management");
  };

  //Handle the response of updateStaffDetailsRequest_ADMIN
  const updateStaffDetailsRequestCB_ADMIN = () => {
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    notifySuccess(`Staff details updated successfully!`);
    navigate("/staff-management");
  };

  const onPhotoUpload = (fileInfo) => {
    if (fileInfo) {
      const formData_ = new FormData();
      formData_.append("file", fileInfo);

      dispatch(
        fileUploadRequest_ADMIN({
          file: formData_,
          callBack: (data) => {
            setFormData((prevData) => ({
              ...prevData,
              photo: data?.url,
            }));

            if (formErrors.photo) {
              setFormErrors((prevErrors) => ({
                ...prevErrors,
                photo: "",
              }));
            }
          },
        })
      );
    } else {
      setFormData((prevData) => ({
        ...prevData,
        photo: fileInfo,
      }));

      if (formErrors.photo) {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          photo: "",
        }));
      }
    }
  };

  return (
    <MainLayout style={{ backgroundColor: Colors.grey_12 }}>
      <div className="staff-management-page">
        <div
          onClick={() => {
            navigate(-1);
          }}
          className="staff-management-back-option"
        >
          <img src={Back} alt="Back" />
          <span className="staff-management-back-text">
            Back to Staff Management
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="add-staff-container">
            <div className="add-staff-title">
              {hasSelectedStaffDetails
                ? "Update Staff Details"
                : "Add Staff Details"}
            </div>
            <hr className="add-staff-divider" />
            <div className="add-staff-input-container">
              <div className="add-staff-input-group">
                <div>
                  <InputBox
                    label="Employee Name"
                    name="employeeName"
                    value={formData.employeeName}
                    onChange={handleChange}
                    className="add-staff-input-box"
                    required
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={formErrors.employeeName} />
                </div>
                <div>
                  <InputBox
                    label="Employee ID"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange}
                    className="add-staff-input-box"
                    required
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={formErrors.employeeId} />
                </div>
              </div>

              <div className="add-staff-input-group">
                <div>
                  <label>Mobile Number *</label>
                  <div className="row mt-1">
                    <div className="col-4">
                      <CustomSelect
                        customStyles={customStyles}
                        options={countryCode}
                        value={countryCodeValue}
                        isClearable={false}
                        onChange={(option) => {
                          setCountryCodeValue(option);
                          setFormData((prevData) => ({
                            ...prevData,
                            country_code: option?.value || "",
                          }));
                        }}                        
                      />
                    </div>
                    <div className="col-8">
                      <InputBox
                        label=""
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (/^\d*$/.test(inputValue)) {
                            handleChange(e);
                          }
                        }}
                        className="add-staff-input-box"
                        required
                        inputStyle={inputStyle}
                        disabled={!countryCodeValue}
                      />
                    </div>
                  </div>
                  <ErrorMessage message={formErrors.mobileNumber} />
                </div>
                <div>
                  <InputBox
                    label="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="add-staff-input-box"
                    required
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={formErrors.email} />
                </div>
              </div>

              <div className="add-staff-input-group">
                <div>
                  <Dropdown
                    label="Role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    options={[{ label: "Assessor", value: 2 }]}
                    className="add-staff-dropdown"
                    required
                    // dropdownStyle={inputStyle}
                    inputStyle={{ color: "#111314", fontWeight: "500" }}
                  />
                  <ErrorMessage message={formErrors.role} />
                </div>
                <div>
                  <InputBox
                    label="LinkedIn Profile Link"
                    name="linkedInProfileLink"
                    value={formData.linkedInProfileLink}
                    onChange={handleChange}
                    className="add-staff-input-box"
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={formErrors.linkedInProfileLink} />
                </div>
              </div>

              <div className="add-staff-input-group">
                <div>
                  <InputBox
                    label="Bio"
                    name="bio"
                    type="textarea"
                    value={formData.bio}
                    onChange={handleChange}
                    className="add-staff-input-box"
                    required
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={formErrors.bio} />
                </div>
              </div>

              <div className="staff-manangement-bio-container">
                <div>
                  {isLoading === "loading" ? (
                    <Loader />
                  ) : (
                    <PhotoUpload
                      photo={formData?.photo}
                      onPhotoUpload={onPhotoUpload}
                    />
                  )}
                  <ErrorMessage message={formErrors.photo} />
                </div>
              </div>
            </div>
          </div>

          <div className="staff-management-form-buttons-container">
            <button
              onClick={() => {
                setFormData(initialFormData);
                setFormErrors(initialFormErrors);
                navigate(-1);
              }}
              type="button"
              className="staff-management-form-cancel-button"
            >
              Cancel
            </button>
            <button type="submit" className="staff-management-form-save-button">
              Save
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddUpdateStaff;
// which db is more conviente for mobile